'use client';

/**
 * ProfileTestimonials — shows published testimonials of a user in their Profile Hub.
 *
 * Spec ref: SPEC-148 (Sprint 72, 2026-09-07) — F-005c: Testimonios embed.
 *
 * Fetches GET /api/testimonials/public?userId={userId}&limit=3
 * Renders up to 3 testimonials in a compact list.
 * Renders nothing if no published testimonials exist.
 *
 * Anti-§6: pure UI — reads from public API, no business logic.
 */

import { useEffect, useState } from 'react';
import { Quote } from 'lucide-react';

interface Testimonial {
  _id: string;
  authorName: string;
  authorTier: string;
  authorCountry: string;
  testimonialText: string;
  mediaType: string;
  monthsActive: number;
  referralCount: number;
  totalEarnings: string;
  networkSize: number;
}

export function ProfileTestimonials({ userId }: { userId: string }) {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId || userId.length !== 24) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    fetch(`/api/testimonials/public?userId=${userId}&limit=3`, { signal: controller.signal })
      .then((res) => res.ok ? res.json() : { data: [] })
      .then((body) => {
        setTestimonials(body.data ?? []);
      })
      .catch(() => {
        // Silent fail — testimonials are non-critical
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [userId]);

  if (loading || testimonials.length === 0) return null;

  return (
    <section
      aria-labelledby="testimonials-heading"
      className="mt-6 pt-6 border-t border-slate-700"
    >
      <h2
        id="testimonials-heading"
        className="text-sm font-semibold text-white inline-flex items-center gap-2 mb-3"
      >
        <Quote className="size-4" aria-hidden="true" />
        Lo que dicen de mí
      </h2>
      <div className="space-y-3">
        {testimonials.map((t) => (
          <div
            key={t._id}
            className="p-4 rounded-lg bg-slate-800/40 border border-slate-700"
          >
            <p className="text-sm text-slate-200 italic leading-relaxed">
              &ldquo;{t.testimonialText}&rdquo;
            </p>
            <div className="flex items-center gap-2 mt-3">
              <span className="text-xs font-medium text-white">{t.authorName}</span>
              <span className="text-xs text-slate-400">
                · {t.authorTier} · {t.authorCountry}
              </span>
              {t.monthsActive > 0 && (
                <span className="text-xs text-slate-400">
                  · {t.monthsActive} meses activo
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
